import React, { useContext } from 'react';
import {
  Button as ButtonComponent,
  CssOverrideType,
  TertiaryButton,
  useMediaQuery,
  useTheme,
} from '@dripcapital/dripui-v1';
import { AnalyticsContext } from 'shared/context/AnalyticsContext';
import { ButtonTypeEnum } from 'enums';
import { DAJ_COLORS } from 'utils';

type Props = CssOverrideType & {
  dataTestId?: string;
  id?: string;
  loading?: boolean;
  type?: string;
  children: React.ReactNode;
  variant?: ButtonTypeEnum | undefined;
  startIcon?:
    | React.ReactNode
    | (<T extends Record<string, unknown>>(props: T) => JSX.Element | null);
  endIcon?:
    | React.ReactNode
    | (<T extends Record<string, unknown>>(props: T) => JSX.Element | null);
  disabled?: boolean;
  component?: React.ElementType<any>;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const AtomicButton: React.FC<Props> = ({
  dataTestId = 'button-element',
  id,
  children,
  loading,
  type = 'button',
  disabled,
  variant = ButtonTypeEnum.CONTAINED,
  startIcon,
  endIcon,
  _sx,
  className,
  onClick,
  component,
}: Props) => {
  const theme = useTheme();

  const { mixpanel } = useContext(AnalyticsContext);

  const padding = theme.spacing(1.5);

  const smLimit = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (dataTestId && dataTestId !== 'button-element') {
      mixpanel.track(dataTestId);
    }
    onClick(e);
  };

  const getVariantStyle = (
    variant: 'text' | 'contained' | 'outlined' | undefined
  ) => {
    if (variant === 'text') {
      return {
        borderRadius: '8px',
        borderColor: 'transparent',
        backgroundColor: DAJ_COLORS.WEALTHY_GREEN_50,
        color: DAJ_COLORS.WEALTHY_GREEN,
      };
    } else if (variant === 'contained') {
      return {
        borderRadius: '8px',
        borderColor: DAJ_COLORS.WEALTHY_GREEN,
        backgroundColor: DAJ_COLORS.WEALTHY_GREEN_600,
        color: DAJ_COLORS.WHITE,
      };
    } else {
      return {
        borderRadius: '8px',
        borderColor: DAJ_COLORS.WEALTHY_GREEN,
        backgroundColor: DAJ_COLORS.WEALTHY_GREEN_50,
        color: DAJ_COLORS.WEALTHY_GREEN,
      };
    }
  };

  if (['outlined', 'contained'].includes(variant)) {
    return (
      <ButtonComponent
        dataTestId={dataTestId}
        id={id}
        type={type}
        loading={loading}
        className={className}
        variant={variant as any}
        disabled={disabled}
        startIcon={startIcon as React.ReactNode}
        endIcon={endIcon as React.ReactNode}
        _sx={{
          paddingTop: padding,
          paddingBottom: padding,
          width: smLimit ? '100%' : 'initial',
          fontSize: theme.spacing(smLimit ? 1.5 : 2),
          textTransform: 'none',
          borderRadius: theme.spacing(1),
          textAlign: 'center',
          cursor: 'pointer',
          ':hover,:active,:focus': getVariantStyle(variant),
          ..._sx,
        }}
        onClick={handleOnClick}
        component={component}
      >
        {children}
      </ButtonComponent>
    );
  }
  return (
    <TertiaryButton
      dataTestId={dataTestId}
      id={id}
      type={type}
      loading={loading}
      className={className}
      disabled={disabled}
      StartIcon={startIcon as any}
      EndIcon={endIcon as any}
      _sx={{
        paddingTop: padding,
        paddingBottom: padding,
        paddingX: '12px',
        width: smLimit ? '100%' : 'initial',
        fontSize: theme.spacing(smLimit ? 1.5 : 2),
        textTransform: 'none',
        borderRadius: theme.spacing(1),
        textAlign: 'center',
        ':hover,:active,:focus': getVariantStyle(variant),
        ..._sx,
      }}
      onClick={handleOnClick}
      component={component}
    >
      {children}
    </TertiaryButton>
  );
};

export default AtomicButton;
